import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiTrash2, FiArrowRight, FiShoppingBag, FiTag } from 'react-icons/fi';
import { useShopping } from '../context/ShoppingContext';
import { useToast } from '../context/ToastContext';
import apiClient from '../utils/apiClient';
import '../styles/Cart.css';

const Cart = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { cartItems, removeFromCart, updateCartQuantity, getTotalPrice, user } = useShopping();
  const [couponCode, setcouponCode] = useState('');
  const [appliedcoupon, setAppliedcoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [availablecoupons, setAvailablecoupons] = useState([]);
  const [applyingcoupon, setApplyingcoupon] = useState(false);

  const subtotal = getTotalPrice();

  // Fetch available coupons based on cart items
  useEffect(() => {
    const fetchcoupons = async () => {
      try {
        // Send cart items to filter coupons by applicable categories/products
        const cartInfo = cartItems.map(item => ({
          id: item.id,
          category: item.category || ''
        }));
        const response = await apiClient.post('/coupons/active', {
          orderTotal: subtotal,
          cartItems: cartInfo
        });
        setAvailablecoupons(response.data || []);
      } catch (error) {
        console.error('Error fetching coupons:', error);
      }
    };
    fetchcoupons();
  }, [subtotal, cartItems]);

  // Recalculate discount when subtotal changes
  useEffect(() => {
    if (appliedcoupon) {
      // Check if coupon is still applicable
      if (appliedcoupon.minOrderValue && subtotal < appliedcoupon.minOrderValue) {
        setAppliedcoupon(null);
        setDiscountAmount(0);
        toast.info('Coupon removed - minimum order value not met');
      } else {
        // Recalculate discount
        const discount = appliedcoupon.discountType === 'percentage'
          ? (subtotal * appliedcoupon.discountValue) / 100
          : appliedcoupon.discountValue;
        setDiscountAmount(discount);
      }
    }
  }, [subtotal, appliedcoupon]);

  const handleCheckout = () => {
    if (!user) {
      navigate('/account', { state: { from: '/checkout' } });
    } else {
      // Pass applied coupon to checkout
      navigate('/checkout', { state: { appliedcoupon, discountAmount } });
    }
  };

  const handleApplycoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingcoupon(true);
    try {
      // Send cart items for validation
      const cartInfo = cartItems.map(item => ({
        id: item.id,
        category: item.category || ''
      }));
      const response = await apiClient.post('/coupons/validate', {
        code: couponCode.toUpperCase(),
        orderTotal: subtotal,
        cartItems: cartInfo
      });

      const coupon = response.data;
      setAppliedcoupon(coupon);

      // Calculate discount
      const discount = coupon.discountType === 'percentage'
        ? (subtotal * coupon.discountValue) / 100
        : coupon.discountValue;
      setDiscountAmount(discount);

      const discountText = coupon.discountType === 'percentage'
        ? `${coupon.discountValue}% off`
        : `₹${coupon.discountValue} off`;
      toast.success(`coupon applied! ${discountText}`);
      setcouponCode('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Invalid coupon code');
      setAppliedcoupon(null);
      setDiscountAmount(0);
    } finally {
      setApplyingcoupon(false);
    }
  };

  const handleRemovecoupon = () => {
    setAppliedcoupon(null);
    setDiscountAmount(0);
    toast.info('Coupon removed');
  };

  const handleApplyAvailablecoupon = (coupon) => {
    if (!coupon.isApplicable) {
      toast.error(`Minimum order of ₹${coupon.minOrderValue} required`);
      return;
    }
    setcouponCode(coupon.code);
    // Auto-apply
    setAppliedcoupon(coupon);
    const discount = coupon.discountType === 'percentage'
      ? (subtotal * coupon.discountValue) / 100
      : coupon.discountValue;
    setDiscountAmount(discount);
    toast.success(`Coupon ${coupon.code} applied!`);
  };

  // Note: Shipping is calculated at checkout based on selected delivery option

  return (
    <div className="cart-page">
      <div className="cart-container">
        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <div className="empty-cart-image">
              <FiShoppingBag size={80} color="#ff3f6c" />
            </div>
            <h2>Hey, it feels so light!</h2>
            <p>There is nothing in your bag. Let's add some items.</p>
            <Link to="/wishlist" className="add-from-wishlist-btn">
              ADD ITEMS FROM WISHLIST
            </Link>
          </div>
        ) : (
          <div className="cart-content">
            <div className="cart-items-section">
              <h2>Cart Items ({cartItems.length})</h2>
              <div className="cart-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="cart-item">
                    <img src={item.images && item.images.length > 0 ? item.images[0] : item.image} alt={item.name} className="item-image" />

                    <div className="item-details">
                      <h3>{item.name}</h3>
                      <p className="seller">By {item.seller}</p>
                      <p className="price">₹{item.price.toLocaleString('en-IN')}</p>
                    </div>

                    <div className="quantity-control">
                      <button onClick={() => updateCartQuantity(item.id, item.quantity - 1)}>−</button>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateCartQuantity(item.id, parseInt(e.target.value))}
                        min="1"
                      />
                      <button onClick={() => updateCartQuantity(item.id, item.quantity + 1)}>+</button>
                    </div>

                    <div className="item-total">
                      <p>₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                    </div>

                    <button
                      className="remove-btn"
                      onClick={() => removeFromCart(item.id)}
                      title="Remove from cart"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>

              <Link to="/wishlist" className="wishlist-add-more">
                <span className="wishlist-add-more-icon">&#9825;</span>
                <span className="wishlist-add-more-text">Add More From Wishlist</span>
                <span className="wishlist-add-more-arrow">&#8250;</span>
              </Link>

              <div className="coupon-section">
                <label><FiTag /> Have an offer code?</label>
                {appliedcoupon ? (
                  <div className="applied-coupon">
                    <span className="coupon-tag">
                      <FiTag /> {appliedcoupon.code} applied
                      <span className="coupon-discount">
                        ({appliedcoupon.discountType === 'percentage'
                          ? `${appliedcoupon.discountValue}% off`
                          : `₹${appliedcoupon.discountValue} off`})
                      </span>
                    </span>
                    <button className="remove-coupon" onClick={handleRemovecoupon}>Remove</button>
                  </div>
                ) : (
                  <>
                    <div className="coupon-input">
                      <input
                        type="text"
                        placeholder="Enter offer/coupon code"
                        value={couponCode}
                        onChange={(e) => setcouponCode(e.target.value.toUpperCase())}
                      />
                      <button onClick={handleApplycoupon} disabled={applyingcoupon}>
                        {applyingcoupon ? 'Applying...' : 'Apply'}
                      </button>
                    </div>
                    {availablecoupons.length > 0 && (
                      <div className="available-coupons">
                        <p className="coupons-title">Available Offers:</p>
                        {availablecoupons.map((v) => (
                          <div
                            key={v.code}
                            className={`coupon-item ${v.isApplicable ? 'applicable' : 'not-applicable'}`}
                            onClick={() => handleApplyAvailablecoupon(v)}
                          >
                            <span className="coupon-code">{v.code}</span>
                            <span className="coupon-value">
                              {v.discountType === 'percentage' ? `${v.discountValue}% off` : `₹${v.discountValue} off`}
                            </span>
                            {v.minOrderValue > 0 && (
                              <span className="coupon-min">Min ₹{v.minOrderValue}</span>
                            )}
                            {!v.isApplicable && <span className="coupon-status">Add more items</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <aside className="cart-summary">
              <h2>Order Summary</h2>

              {/* Individual product costs */}
              <div className="summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-row item-row">
                    <span className="item-name">{item.name} {item.quantity > 1 && `(×${item.quantity})`}</span>
                    <span>₹{(item.price * item.quantity).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>

              <div className="summary-divider"></div>

              <div className="summary-row subtotal-row">
                <span>Subtotal ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})</span>
                <span>₹{subtotal.toLocaleString('en-IN')}</span>
              </div>

              {appliedcoupon && discountAmount > 0 && (
                <div className="summary-row discount">
                  <span>coupon ({appliedcoupon.code})</span>
                  <span>-₹{discountAmount.toLocaleString('en-IN')}</span>
                </div>
              )}

              <div className="summary-row shipping-note">
                <span>Shipping</span>
                <span style={{ color: '#666', fontSize: '0.9em' }}>Calculated at checkout</span>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-total">
                <span>Estimated Total</span>
                <span>₹{(subtotal - discountAmount).toLocaleString('en-IN')}</span>
              </div>

              <button onClick={handleCheckout} className="checkout-btn">Proceed to Checkout</button>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
